<script lang="ts">
  /*
  A draggable component that can be used to wrap any element and make it draggable.
  It uses a portal to create a ghost element that follows the mouse cursor while dragging.
  When dropped, it either snaps back to its original position or stays where dropped based on props.
  It also supports inertia effect when dropped on a droppable area.
*/

  import { onMount /* , onDestroy */ } from 'svelte';
  import { dragItem } from '../../store';
  import Portal from 'svelte-portal';
  import { handOffFlight } from '../../animation';

  let {
    left = 0,
    top = 0,
    clazz = '',
    onClick = () => {},
    data = {},
    freeMovement = true,
    locked = false,
    /** Fires when a touch is held still on this item. See holdDelay. */
    onHold = () => {},
    /** Fires when a hold ends, whether by release or by turning into a drag. */
    onHoldEnd = () => {},
    holdDelay = 350,
    children,
  } = $props();

  let vx = 0;
  let vy = 0;
  let lastX = 0;
  let lastY = 0;
  let mouseOffsetX = 0;
  let mouseOffsetY = 0;

  let element: HTMLElement;
  let elementPortal: HTMLElement;
  let parentOffsetX = 0;
  let parentOffsetY = 0;
  // How far the drag has carried the element from left/top. Written to as it
  // moves; being derived from left/top, it falls back to 0 whenever the element
  // is given a new position. The `* 0` is what makes that reset, not a leftover.
  let moveX = $derived(left * 0);
  let moveY = $derived(top * 0);

  const acceleration = 0.85;
  const t = Math.log(1e-3) / Math.log(acceleration);

  /**
   * How far a finger may travel and still count as a press rather than a drag.
   * A finger never holds perfectly still, and any movement at all used to start
   * a drag, so without this a hold could not survive its own contact jitter.
   */
  const DRAG_SLOP = 10;

  let dragging = $state(false);
  let mousedown = $state(false);
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let held = false;
  let transitioning = $state(false);
  /**
   * True until this item has been put in its starting place. onMount centres it
   * 10ms in, and .draggable transitions top/left over 420ms -- so without this a
   * freshly created die visibly slides in from the corner of its container, and
   * anything measuring where it landed measures it mid-slide.
   */
  let placing = $state(true);
  let positionString = $derived(getPositionStyle());
  let positionString2 = $derived(getPositionStyle(true));

  let ghostHtml: string = $state('');

  function onMouseDown(event: MouseEvent) {
    if (locked) return;
    event.stopPropagation();
    event.preventDefault();

    calculateParentOffset();
    ghostHtml = element.innerHTML;
    lastX = event.clientX;
    lastY = event.clientY;
    vx = 0;
    vy = 0;
    mouseOffsetX = event.clientX - (left + moveX + parentOffsetX);
    mouseOffsetY = event.clientY - (top + moveY + parentOffsetY);
    mousedown = true;
    dragging = false;

    // Register mousemove listener only when dragging starts
    window.addEventListener('mousemove', onMouseMove);
  }

  /**
   * A press that never became a drag. A tap can move the item to another list
   * too -- tapping a die locks it -- and that journey is just as hidden as a
   * dropped one, clipped or painted underneath. So it leaves a ghost behind in
   * the same way, for the move to pick up. When nothing moves, the ghost sits
   * exactly over the item and is gone again a moment later.
   */
  function tap() {
    if (element) {
      // On the board this element is 0x0, with the die absolutely positioned
      // inside it, so copy whichever of the two actually has a box.
      const { width, height } = element.getBoundingClientRect();
      const visible = width && height ? element : element.firstElementChild;
      if (visible) handOffFlight(data?.id, visible as HTMLElement);
    }
    onClick();
  }

  /** The keyboard's way to do what a tap does. */
  function onKeyDown(event: KeyboardEvent) {
    if (locked || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    tap();
  }

  // Handler for if the element is clicked but not dragged
  function onMouseUp(event: MouseEvent) {
    if (locked || dragging) return;
    event.stopPropagation();
    event.preventDefault();

    // Always drop the move listener: this path is only reached when no drag
    // happened, and the two early returns above meant the removal at the end
    // of this function could never run at all.
    window.removeEventListener('mousemove', onMouseMove);

    if (!dragging) {
      tap();
      mousedown = false;
    }
  }

  function centerInParent() {
    if (!element || !element.parentElement) return;
    const parentRect = element.parentElement.getBoundingClientRect();
    const elemRect = element.getBoundingClientRect();
    left = (parentRect.width - elemRect.width) / 2;
    top = (parentRect.height - elemRect.height) / 2;
    moveX = 0;
    moveY = 0;
    updatePositionString();
  }

  // This is called when dropped on a droppable area by the droppable component
  export function dropHandler(event: MouseEvent, onDroppable: boolean, onSelf: boolean = false) {
    if (!dragging) {
      tap();
      return;
    }
    calculateParentOffset();

    if (onDroppable) {
      if (onSelf && !freeMovement) {
        // If dropped on self, just center in parent if needed
        centerInParent();
      } else {
        droppedOnDroppable();
      }
    } else {
      moveX = 0;
      moveY = 0;
    }

    if (!freeMovement) {
      centerInParent();
    }

    mousedown = false;

    // Keep the ghost alive past the end of the drag, before `dragging` flips
    // and tears the portal's contents down. It stands in for the die while the
    // die travels somewhere it cannot be seen.
    if (elementPortal) {
      handOffFlight(data?.id, elementPortal, element);
    }

    dragging = false;
    updatePositionString();
    if (elementPortal) {
      elementPortal.style.display = 'none';
    }

    // Deregister mousemove listener when drag ends
    window.removeEventListener('mousemove', onMouseMove);
  }

  // Handle mouse move globally to allow dragging outside the element
  function onMouseMove(event: MouseEvent) {
    if (mousedown) {
      dragging = true;
      dragItem.set({ data, dropHandler });
    }
    if (dragging) {
      moveX += event.movementX;
      moveY += event.movementY;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      vx = dx;
      vy = dy;
      updatePositionString();
    }
  }

  function droppedOnDroppable() {
    if (!mousedown) return;
    if (!dragging) {
      tap();
    }
    mousedown = false;
    dragging = false;
    const dx = (vx * (1 - acceleration ** t)) / (1 - acceleration);
    const dy = (vy * (1 - acceleration ** t)) / (1 - acceleration);
    // moveX += dx;
    // moveY += dy;
    vx = 0;
    vy = 0;
    updatePositionString();
  }

  function getPositionStyle(withParentOffset = false) {
    if (!freeMovement && dragging == false) {
      // Consider resetting position here if needed
    }
    if (left === 0 && top === 0) {
      return '';
    }
    const parentX = withParentOffset ? parentOffsetX : 0;
    const parentY = withParentOffset ? parentOffsetY : 0;
    const x = left + moveX + parentX;
    const y = top + moveY + parentY;
    return `left: ${x}px; top: ${y}px;`;
  }

  function updatePositionString() {
    positionString = getPositionStyle();
    positionString2 = getPositionStyle(true);
  }

  function resize() {
    updatePositionString();
  }

  function calculateParentOffset() {
    if (element?.parentElement) {
      const rect = element.parentElement.getBoundingClientRect();
      parentOffsetX = rect.left;
      parentOffsetY = rect.top;

      updatePositionString();
    }
  }

  onMount(() => {
    window.addEventListener('resize', resize);
    updatePositionString();

    setTimeout(() => {
      if (element?.parentElement) {
        calculateParentOffset();
        updatePositionString();
        // Center on mount if freeMovement is false AND left/top are 0 or undefined
        if (!freeMovement && !left && !top) {
          centerInParent();
        }
      }

      // Placed. Let the position land without a transition first, then allow
      // them again for genuine moves later on.
      requestAnimationFrame(() => {
        placing = false;
      });
    }, 10);

    window.addEventListener('dropfail', onDropFail);

    return () => {
      window.removeEventListener('resize', resize);
      // Was never removed, and being an inline function could not have been.
      // Every <Draggable> ever mounted left one of these on window, each
      // holding this component alive -- and dice are rebuilt every turn, so
      // they accumulated for as long as the tab was open.
      window.removeEventListener('dropfail', onDropFail);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      if (holdTimer !== null) clearTimeout(holdTimer);
    };
  });

  function onDropFail(event: any) {
    if (dragging) {
      dropHandler(event.detail.event, false);
    }
  }

  function transitionStart() {
    // transitioning = true;
  }
  function transitionEnd() {
    // transitioning = false;
  }

  /**
   * A press only becomes a hold once it has stayed put for holdDelay. Touch
   * only: a pointer can hover, so on a desktop the hover already does this job,
   * and a mouse held down is how a drag begins rather than a request to look.
   */
  function startHold() {
    cancelHold();
    holdTimer = setTimeout(() => {
      holdTimer = null;
      held = true;
      onHold();
    }, holdDelay);
  }

  /** Drops a pending hold, and closes an open one. */
  function cancelHold() {
    if (holdTimer !== null) {
      clearTimeout(holdTimer);
      holdTimer = null;
    }
    if (held) {
      held = false;
      onHoldEnd();
    }
  }

  function onTouchStart(event: TouchEvent) {
    if (locked) return;
    event.stopPropagation();
    // event.preventDefault();
    calculateParentOffset();
    const touch = event.touches[0];
    ghostHtml = element.innerHTML;
    lastX = touch.clientX;
    lastY = touch.clientY;
    vx = 0;
    vy = 0;
    mouseOffsetX = touch.clientX - (left + moveX + parentOffsetX);
    mouseOffsetY = touch.clientY - (top + moveY + parentOffsetY);
    mousedown = true;
    dragging = false;
    startHold();
    window.addEventListener('touchmove', onTouchMove, { passive: false });
  }

  function onTouchEnd(event: TouchEvent) {
    if (locked) return;

    // Same fix as onMouseUp: the early return above used to skip the removal
    // below entirely, so every finished touch drag left one of these behind.
    window.removeEventListener('touchmove', onTouchMove);

    if (dragging) return;
    event.stopPropagation();
    event.preventDefault();

    // A hold has already done this press's job. Releasing it puts the die back
    // exactly as it was -- it must not also count as the tap that locks it.
    const wasHeld = held;
    cancelHold();
    if (wasHeld) {
      mousedown = false;
      return;
    }

    tap();
    mousedown = false;
  }

  /**
   * The system took the touch away -- a call, the app switcher, a gesture from
   * the screen edge. Without this the peek would be left open with no finger
   * on screen to close it.
   */
  function onTouchCancel() {
    window.removeEventListener('touchmove', onTouchMove);
    cancelHold();
    mousedown = false;
  }

  function onTouchMove(event: TouchEvent) {
    const touch = event.touches[0];
    if (!touch) return;

    if (mousedown && !dragging) {
      // lastX/lastY still hold the point the finger landed on, because nothing
      // below updates them until a drag has actually begun.
      const dx = touch.clientX - lastX;
      const dy = touch.clientY - lastY;
      if (Math.hypot(dx, dy) < DRAG_SLOP) return;

      cancelHold();
      dragging = true;
      dragItem.set({ data, dropHandler });

      // Catch the die up to the finger, so it starts the drag underneath it
      // rather than a slop radius behind.
      moveX += dx;
      moveY += dy;
      lastX = touch.clientX;
      lastY = touch.clientY;
      updatePositionString();
      return;
    }

    if (dragging) {
      const dx = touch.clientX - lastX;
      const dy = touch.clientY - lastY;
      moveX += dx;
      moveY += dy;
      lastX = touch.clientX;
      lastY = touch.clientY;
      vx = dx;
      vy = dy;
      updatePositionString();
    }
  }
</script>

<div
  style={left !== undefined ? positionString : ''}
  class="draggable {clazz} {locked ? 'locked' : ''} {dragging
    ? 'dragging hiding'
    : ''} {transitioning ? 'transitioning' : ''}"
  bind:this={element}
  onmousedown={onMouseDown}
  onmouseup={onMouseUp}
  ontouchstart={onTouchStart}
  ontouchend={onTouchEnd}
  ontouchcancel={onTouchCancel}
  onkeydown={onKeyDown}
  role="button"
  tabindex="0"
  ontransitionstart={transitionStart}
  ontransitionend={transitionEnd}
>
  {@render children()}
</div>

<Portal target="body">
  <div
    bind:this={elementPortal}
    style={left !== undefined ? positionString2 : ''}
    class="draggable portal {clazz} {dragging ? 'dragging' : 'asd'}"
  >
    {#if dragging && ghostHtml}
      {@html ghostHtml}
    {/if}
  </div>
</Portal>

<style lang="less">
  .draggable {
    user-select: none;
    cursor: move;
    position: absolute;
    // A finger on a die drags the die. Left to the browser, it would pan
    // whatever scrolls underneath -- the technique list -- and the browser
    // commits to a pan before onTouchMove could call it off. A locked item
    // cannot be dragged, so a finger on one still scrolls.
    touch-action: none;

    &.locked {
      touch-action: auto;
    }
    transition:
      top 420ms ease-out,
      left 420ms ease-out,
      opacity 0s !important;

    // No transition while being put in place, so it appears where it belongs
    // rather than sliding there.
    &.placing {
      transition: none !important;
    }

    &.dragging {
      transition: none !important;
      z-index: 999999999999;
    }

    &.hiding {
      opacity: 0;
      transition: opacity 0s !important;
    }

    &.transitioning {
      pointer-events: none;
      z-index: 999999999;
    }
  }
</style>
